import { Box, HStack, IconButton, Spacer, Text } from '@chakra-ui/react'
import truncate from 'lodash/truncate'
import type { FC } from 'react'
import { memo, useState } from 'react'
import { FiTrash2 } from 'react-icons/fi'

import { Avatar } from '@app/components/ui/avatar'
import { useColorModeValue } from '@app/components/ui/color-mode'
import { Tooltip } from '@app/components/ui/tooltip'
import AccountDeleteButton from '@app/pages/settings/organization/components/accountDeleteButton'
import AccountSwitchButton from '@app/pages/settings/user/components/accountSwitchButton'
import type { UserData } from '@app/types'
import type { Account } from '@graphql/types'

const colorPalette = ['red', 'blue', 'green', 'yellow', 'purple', 'orange']

const pickPalette = (name: string) => {
  const index = name.charCodeAt(0) % colorPalette.length
  return colorPalette[index]
}

interface Props {
  account: Account
  user: UserData['user']
}

const AccountRailAccount: FC<Props> = ({ account, user }) => {
  const [showActions, setShowActions] = useState(false)
  const selected = account?.organizationId === user?.organization?.id
  const borderColor = useColorModeValue('border.muted', 'border.emphasized')

  return (
    <HStack
      cursor="pointer"
      boxSizing="border-box"
      w="full"
      color={selected ? 'fg' : 'fg.muted'}
      fontWeight={selected ? 'semibold' : 'normal'}
      borderBottom="1px solid transparent"
      _hover={{
        color: 'fg',
        fontWeight: 'semibold',
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: borderColor
      }}
      onMouseEnter={() => {
        if (!selected) {
          setShowActions(true)
        }
      }}
      onMouseLeave={() => {
        setShowActions(false)
      }}
    >
      <>
        <AccountSwitchButton account={account} key={account.id} confirm={false}>
          <HStack>
            <Avatar
              shape="rounded"
              size="2xs"
              colorPalette={account.organizationLogoUrl ? 'transparent' : pickPalette(account.organizationName)}
              key={account.id}
              name={account.organizationName}
              src={account.organizationLogoUrl}
            />
            <Text transition="all 0.3s" fontSize="sm">
              {truncate(account.organizationName, {
                length: 32
              })}
            </Text>
          </HStack>
        </AccountSwitchButton>
        <Spacer h={3} />
        <AccountDeleteButton account={account} body="Are you sure you want to remove yourself from this account?">
          <Box visibility={showActions ? 'visible' : 'hidden'} opacity={showActions ? 1 : 0} transition="all 0.2s">
            <Tooltip content="Delete account" showArrow>
              <IconButton aria-label="Delete account" variant="plain" size="sm" _hover={{ color: 'red.500' }}>
                <FiTrash2 />
              </IconButton>
            </Tooltip>
          </Box>
        </AccountDeleteButton>
      </>
    </HStack>
  )
}

export default memo(AccountRailAccount)
